import { AfterViewInit, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Doctor, NameFormat } from 'src/API';
import {
  buildClinicianPrefillPatch,
  ClinicianPrefillFieldValue,
  ClinicianPrefillPatch,
  ConsentClinicianPrefillField,
  clinicianFieldDotPath,
  getClinicianOnlyFields
} from 'src/app/core/api/consent-form.util';
import { ConfigType, FormTemplateSchema, ValueConfig } from 'src/app/form-template/form-template-model';
import { CustomValidator } from 'src/app/intake-form/intake-form-validators';
import { requiredNonWhitespaceValidator } from '../shared-validators/required-non-whitespace-validator';
import { ChooseDoctorFromClinicComponent } from '../choose-doctor-from-clinic/choose-doctor-from-clinic.component';
import { PersonNamePipe } from '../shared-pipes/person-name.pipe';

export interface ConsentClinicianPrefillDialogData {
  schema: FormTemplateSchema;
  defaultPractitionerId?: string;
  /** Friendly questionnaire/form name shown under the dialog title (optional). */
  questionnaireDisplayName?: string;
}

export interface ConsentClinicianPrefillDialogResult {
  clinicianPrefill: ClinicianPrefillPatch | null;
}

interface PrefillOptionViewModel {
  key: string;
  label: string;
}

/** Precomputed field row for the template (no method/getter calls in HTML). */
interface PrefillFieldViewModel {
  clinicianField: ConsentClinicianPrefillField;
  fieldKey: string;
  label: string;
  type: ConfigType;
  isPractitioner: boolean;
  dialogControlPath: string;
  nestedFieldPath: string;
  radioOptions: PrefillOptionViewModel[];
  checkboxOptions: PrefillOptionViewModel[];
  otherOptionKey: string | null;
  otherDetailControlPath: string | null;
  showOtherDetail: boolean;
  showRequiredError: boolean;
}

@Component({
  selector: 'csi-consent-clinician-prefill-dialog',
  templateUrl: './consent-clinician-prefill-dialog.component.html',
  styleUrls: ['./consent-clinician-prefill-dialog.component.css'],
  providers: [PersonNamePipe]
})
export class ConsentClinicianPrefillDialogComponent implements OnInit, OnDestroy, AfterViewInit {
  public fieldViewModels: PrefillFieldViewModel[] = [];
  public formGroup = new FormGroup({});
  public submitted = false;
  public ConfigType = ConfigType;
  public practitionerDoctorIdControl: FormControl | null = null;
  public isPractitionerPickerLoading = false;
  public questionnaireDisplayName = '';
  public showQuestionnaireDisplayName = false;

  @ViewChild('doctorPicker', { static: false }) doctorPicker: ChooseDoctorFromClinicComponent;

  constructor(
    public dialogRef: MatDialogRef<
      ConsentClinicianPrefillDialogComponent,
      ConsentClinicianPrefillDialogResult | null
    >,
    @Inject(MAT_DIALOG_DATA) public data: ConsentClinicianPrefillDialogData,
    private personNamePipe: PersonNamePipe,
    private changeDetector: ChangeDetectorRef
  ) {}

  public static open(
    matDialog: MatDialog,
    data: ConsentClinicianPrefillDialogData
  ): MatDialogRef<
    ConsentClinicianPrefillDialogComponent,
    ConsentClinicianPrefillDialogResult | null
  > {
    return matDialog.open(ConsentClinicianPrefillDialogComponent, {
      width: '520px',
      maxWidth: '95vw',
      disableClose: true,
      data
    });
  }

  ngOnInit() {
    const providedDisplayName =
      this.data.questionnaireDisplayName && this.data.questionnaireDisplayName.trim();
    const schemaDisplayName =
      this.data.schema && this.data.schema.name && String(this.data.schema.name).trim();
    this.questionnaireDisplayName = providedDisplayName || schemaDisplayName || '';
    this.showQuestionnaireDisplayName = !!this.questionnaireDisplayName;

    const clinicianOnlyFields = getClinicianOnlyFields(this.data.schema);
    this.fieldViewModels = clinicianOnlyFields.map(clinicianField =>
      this.buildFieldViewModel(clinicianField)
    );
    this.fieldViewModels.forEach(fieldViewModel => {
      if (fieldViewModel.isPractitioner) {
        this.practitionerDoctorIdControl = new FormControl(
          this.data.defaultPractitionerId || null,
          fieldViewModel.clinicianField.config.required ? Validators.required : null
        );
        this.formGroup.addControl(
          fieldViewModel.dialogControlPath,
          this.practitionerDoctorIdControl
        );
        this.watchRequiredError(fieldViewModel, this.practitionerDoctorIdControl);
        this.isPractitionerPickerLoading = true;
        return;
      }
      if (fieldViewModel.type === ConfigType.checkbox) {
        this.addCheckboxFieldControls(fieldViewModel);
      } else {
        const fieldControl = new FormControl(
          null,
          fieldViewModel.clinicianField.config.required
            ? fieldViewModel.type === ConfigType.input
              ? requiredNonWhitespaceValidator
              : Validators.required
            : null
        );
        this.formGroup.addControl(fieldViewModel.dialogControlPath, fieldControl);
        this.watchRequiredError(fieldViewModel, fieldControl);
      }
    });
  }

  ngOnDestroy() {}

  ngAfterViewInit() {
    this.changeDetector.detectChanges();
    if (!this.doctorPicker) {
      this.isPractitionerPickerLoading = false;
      return;
    }
    this.doctorPicker.clinicDoctors$.pipe(untilDestroyed(this)).subscribe(() => {
      this.isPractitionerPickerLoading = !!this.doctorPicker.isLoading;
      this.changeDetector.detectChanges();
    });
  }

  onCancel() {
    this.dialogRef.close(null);
  }

  onConfirm() {
    this.submitted = true;
    this.formGroup.markAllAsTouched();
    this.refreshRequiredErrors();
    if (this.formGroup.invalid) {
      return;
    }
    if (this.isPractitionerPickerLoading) {
      return;
    }

    const fieldValuesByNestedPath: {
      [nestedFieldPath: string]: ClinicianPrefillFieldValue;
    } = {};
    for (let fieldIndex = 0; fieldIndex < this.fieldViewModels.length; fieldIndex++) {
      const fieldViewModel = this.fieldViewModels[fieldIndex];
      let fieldValue: ClinicianPrefillFieldValue = this.formGroup.get(
        fieldViewModel.dialogControlPath
      ).value;

      if (fieldViewModel.isPractitioner) {
        const practitionerDisplayName = this.resolvePractitionerDisplayName(fieldValue as string);
        if (!practitionerDisplayName) {
          const practitionerControl = this.formGroup.get(fieldViewModel.dialogControlPath);
          if (practitionerControl) {
            practitionerControl.setErrors({ required: true });
          }
          fieldViewModel.showRequiredError = true;
          return;
        }
        fieldValue = practitionerDisplayName;
      }

      if (
        fieldViewModel.otherOptionKey &&
        fieldViewModel.otherDetailControlPath &&
        fieldValue &&
        typeof fieldValue === 'object' &&
        fieldValue[fieldViewModel.otherOptionKey]
      ) {
        const otherDetailSupportingFieldId = this.getOtherSupportingFieldId(
          fieldViewModel.clinicianField,
          fieldViewModel.otherOptionKey
        );
        fieldValue = {
          ...fieldValue,
          [fieldViewModel.otherOptionKey + '-' + otherDetailSupportingFieldId]: this.formGroup.get(
            fieldViewModel.otherDetailControlPath
          ).value
        };
      }
      fieldValuesByNestedPath[fieldViewModel.nestedFieldPath] = fieldValue;
    }
    this.dialogRef.close({
      clinicianPrefill: buildClinicianPrefillPatch(
        this.fieldViewModels.map(fieldViewModel => fieldViewModel.clinicianField),
        fieldValuesByNestedPath
      )
    });
  }

  private buildFieldViewModel(
    clinicianField: ConsentClinicianPrefillField
  ): PrefillFieldViewModel {
    const dialogControlPath = clinicianFieldDotPath(clinicianField).replace(/\./g, '__');
    const typeConfig = clinicianField.config.typeConfig;
    const fieldType = typeConfig.type as ConfigType;
    // Narrow on typeConfig.type (not a copied fieldType) so valuesConfig is typed.
    let radioOptions: PrefillOptionViewModel[] = [];
    let checkboxOptions: PrefillOptionViewModel[] = [];
    if (typeConfig.type === ConfigType.radio) {
      radioOptions = this.buildOptionViewModels(typeConfig.valuesConfig || {});
    } else if (typeConfig.type === ConfigType.checkbox) {
      checkboxOptions = this.buildOptionViewModels(typeConfig.valuesConfig || {});
    }
    return {
      clinicianField,
      fieldKey: clinicianField.fieldKey,
      label: clinicianField.config.label,
      type: fieldType,
      isPractitioner: clinicianField.fieldKey === 'practitionerName',
      dialogControlPath,
      nestedFieldPath: clinicianFieldDotPath(clinicianField),
      radioOptions,
      checkboxOptions,
      otherOptionKey: null,
      otherDetailControlPath: null,
      showOtherDetail: false,
      showRequiredError: false
    };
  }

  private buildOptionViewModels(valuesConfig: {
    [optionKey: string]: ValueConfig;
  }): PrefillOptionViewModel[] {
    return Object.keys(valuesConfig)
      .sort((optionKeyA, optionKeyB) => {
        const sortOrderA = valuesConfig[optionKeyA].sort != null ? valuesConfig[optionKeyA].sort : 0;
        const sortOrderB = valuesConfig[optionKeyB].sort != null ? valuesConfig[optionKeyB].sort : 0;
        return sortOrderA - sortOrderB;
      })
      .map(optionKey => ({
        key: optionKey,
        label: (valuesConfig[optionKey] && valuesConfig[optionKey].label) || optionKey
      }));
  }

  private watchRequiredError(
    fieldViewModel: PrefillFieldViewModel,
    fieldControl: FormControl | FormGroup
  ): void {
    fieldControl.statusChanges.pipe(untilDestroyed(this)).subscribe(() => {
      fieldViewModel.showRequiredError = this.submitted && fieldControl.invalid;
    });
  }

  private refreshRequiredErrors(): void {
    this.fieldViewModels.forEach(fieldViewModel => {
      const fieldControl = this.formGroup.get(fieldViewModel.dialogControlPath);
      fieldViewModel.showRequiredError = !!(
        this.submitted &&
        fieldControl &&
        fieldControl.invalid
      );
    });
  }

  private addCheckboxFieldControls(fieldViewModel: PrefillFieldViewModel): void {
    const clinicianField = fieldViewModel.clinicianField;
    if (clinicianField.config.typeConfig.type !== ConfigType.checkbox) {
      return;
    }
    const valuesConfig = clinicianField.config.typeConfig.valuesConfig || {};
    const checkboxOptionControls = Object.keys(valuesConfig).reduce(
      (controlsByOptionKey: { [optionKey: string]: FormControl }, optionKey) => {
        return {
          ...controlsByOptionKey,
          [optionKey]: new FormControl(false)
        };
      },
      {}
    );
    const checkboxFormGroup = new FormGroup(
      checkboxOptionControls,
      clinicianField.config.required ? CustomValidator.atLeastOneCheckBoxRequired : null
    );
    this.formGroup.addControl(fieldViewModel.dialogControlPath, checkboxFormGroup);
    this.watchRequiredError(fieldViewModel, checkboxFormGroup);

    const otherOptionKey = this.findOtherOptionKey(valuesConfig);
    if (!otherOptionKey) {
      return;
    }
    fieldViewModel.otherOptionKey = otherOptionKey;
    const otherDetailControlPath = fieldViewModel.dialogControlPath + '__otherDetail';
    fieldViewModel.otherDetailControlPath = otherDetailControlPath;
    const otherOptionConfig = valuesConfig[otherOptionKey] as ValueConfig;
    const isOtherDetailRequired =
      otherOptionConfig.subFieldIdList &&
      otherOptionConfig.subFieldIdList.some(subField => subField.required);
    // Only require free-text once "Other" is checked (not while hidden).
    this.formGroup.addControl(otherDetailControlPath, new FormControl(null));

    checkboxFormGroup.controls[otherOptionKey].valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(isOtherChecked => {
        fieldViewModel.showOtherDetail = !!isOtherChecked;
        const otherDetailControl = this.formGroup.get(otherDetailControlPath);
        if (!otherDetailControl) {
          return;
        }
        if (isOtherChecked && isOtherDetailRequired) {
          otherDetailControl.setValidators(requiredNonWhitespaceValidator);
        } else {
          otherDetailControl.clearValidators();
          otherDetailControl.setValue(null);
        }
        otherDetailControl.updateValueAndValidity();
      });
  }

  private resolvePractitionerDisplayName(doctorId: string): string {
    if (!doctorId || !this.doctorPicker) {
      return '';
    }
    // Exact match only — never fall back to clinic owner (getSelectedDoctor does that).
    let selectedDoctor = this.doctorPicker.findDoctor(doctorId);
    if (
      !selectedDoctor &&
      this.doctorPicker.currentSelectedDoctor &&
      this.doctorPicker.currentSelectedDoctor.id === doctorId
    ) {
      selectedDoctor = this.doctorPicker.currentSelectedDoctor;
    }
    if (!selectedDoctor) {
      return '';
    }
    const doctorForDisplayName = {
      ...selectedDoctor,
      __typename: 'Doctor'
    } as Doctor;
    const formattedName = this.personNamePipe.transform(
      doctorForDisplayName,
      (selectedDoctor.displayedNameFormat as NameFormat) || NameFormat.TitleWithFullName
    );
    if (formattedName) {
      return formattedName;
    }
    return [selectedDoctor.firstName, selectedDoctor.lastName].filter(Boolean).join(' ').trim();
  }

  private findOtherOptionKey(valuesConfig: { [optionKey: string]: ValueConfig }): string | null {
    if (valuesConfig['other']) {
      return 'other';
    }
    const optionKeys = Object.keys(valuesConfig);
    for (let optionIndex = 0; optionIndex < optionKeys.length; optionIndex++) {
      const optionKey = optionKeys[optionIndex];
      const optionConfig = valuesConfig[optionKey];
      if (optionConfig && optionConfig.subFieldIdList && optionConfig.subFieldIdList.length > 0) {
        return optionKey;
      }
    }
    return null;
  }

  private getOtherSupportingFieldId(
    clinicianField: ConsentClinicianPrefillField,
    otherOptionKey: string
  ): string {
    if (clinicianField.config.typeConfig.type !== ConfigType.checkbox) {
      return 'conditionOtherDetail';
    }
    const otherOptionConfig = clinicianField.config.typeConfig.valuesConfig[
      otherOptionKey
    ] as ValueConfig;
    if (otherOptionConfig && otherOptionConfig.subFieldIdList && otherOptionConfig.subFieldIdList[0]) {
      return otherOptionConfig.subFieldIdList[0].id;
    }
    return 'conditionOtherDetail';
  }
}
